import { Controller } from "stimulus";
import {
  validCID,
  validContactNumber,
  validDOB,
  validEmail,
  validMandatory,
  validIssueAndExpiresDate,
  validFileUpload,
  validBnbAccount,
} from "../services/field-validation_services";

export default class extends Controller {
  static targets = [
    "stepper",
    "stepperDiv",
    "stepperIcon",
    "nomineesContainer",
    "personalDetailContainer",
    "personalDetailUploadContainer",
    "incomeDetailContainer",
    "spouseDetailContainer",
  ];
  static values = { presentStep: Number };

  connect() {
    this.presentStepValue = 1;
    this.updateStepper(this.presentStepValue);
    this.checkStepperCompletion();
  }

  randomStep(event) {
    const step = parseInt(event.target.dataset.step);
    this.presentStepValue = step;
    this.toggleStep(step);
    this.updateStepper(step);
  }

  nextStep() {
    const step = this.presentStepValue + 1;
    this.presentStepValue = step > 10 ? 10 : step;
    this.toggleStep(this.presentStepValue);
    this.updateStepper(this.presentStepValue);
  }

  previousStep() {
    const step = this.presentStepValue - 1;
    this.presentStepValue = step < 1 ? 1 : step;
    this.toggleStep(this.presentStepValue);
    this.updateStepper(this.presentStepValue);
  }

  toggleStep(step) {
    for (let i = 1; i <= 10; i++) {
      const content = document.getElementById(`onboard-step${i}-content`);
      if (i !== step) {
        content.classList.add("hidden");
      } else {
        content.classList.remove("hidden");
      }
    }
    this.checkStepperCompletion();
  }

  checkStepperCompletion() {
    this.stepperIconTargets.forEach((_, index) => {
      const status = this[`checkStep${index + 1}`]();
      this.updateStepperIcon(status, index + 1);
    });
  }

  validateBeforeSubmit(event) {
    let incompleteCount = false;
    for (let index = 0; index < this.stepperIconTargets.length; index++) {
      if (!this[`checkStep${index + 1}`]()) {
        incompleteCount = true;
        break;
      }
    }

    if (incompleteCount) {
      event.preventDefault();
      alert("Please complete all fields before submitting the form.");
    }
  }

  updateStepper(step) {
    for (let i = 1; i <= this.stepperTargets.length; i++) {
      if (i !== step) {
        this.stepperDivTargets[i - 1].classList.remove("bg-[rgba(0,0,0,0.07)]");
        this.stepperTargets[i - 1].classList.add("hidden");
      } else {
        this.stepperDivTargets[i - 1].classList.add("bg-[rgba(0,0,0,0.07)]");
        this.stepperTargets[i - 1].classList.remove("hidden");
      }
    }
  }

  updateStepperIcon(status, step) {
    const iconElement = this.stepperIconTargets[step - 1];

    if (this.presentStepValue === step) {
      iconElement.innerHTML =
        '<i class="material-symbols-outlined !text-[18px] text-bnb_red">edit</i>';
      iconElement.classList.remove("bg-bnb_blue");
    } else if (status) {
      iconElement.innerHTML =
        '<i class="material-symbols-outlined text-white">check</i>';
      iconElement.classList.add("bg-bnb_blue");
    } else {
      iconElement.innerHTML = `<span class="text-bnb_red">${step}</span>`;
      iconElement.classList.remove("bg-bnb_blue");
    }
  }

  checkStep1() {
    return true;
  }

  checkStep2() {
    return true;
  }

  checkStep3() {
    return this.validateNomineeForms();
  }

  checkStep4() {
    return this.validatePersonalDetailForms();
  }

  checkStep5() {
    return true;
  }

  checkStep6() {
    return true;
  }

  checkStep7() {
    return this.validateIncomeDetailForms();
  }

  checkStep8() {
    return this.validateSpouseDetailForms();
  }

  checkStep9() {
    return this.validateConsentLiability();
  }

  checkStep10() {
    return this.validatePrivacyPolicy();
  }

  validateNomineeForms() {
    let allValid = true;
    let totalPercentage = 0;

    this.nomineesContainerTarget
      .querySelectorAll(".nominee-form")
      .forEach((form, index) => {
        const name = form.querySelector(
          'input[name^="[nominee]["][name$="][name]"]'
        );
        const dob = form.querySelector(
          'input[name^="[nominee]["][name$="][dob]"]'
        );
        const relationship = form.querySelector(
          'input[name^="[nominee]["][name$="][relationship]"]'
        );
        const cid = form.querySelector(
          'input[name^="[nominee]["][name$="][cid]"]'
        );
        const contactNo = form.querySelector(
          'input[name^="[nominee]["][name$="][contact_no]"]'
        );
        const sharePercentage = form.querySelector(
          'input[name^="[nominee]["][name$="][share_percentage]"]'
        );

        totalPercentage += parseFloat(sharePercentage.value);

        if (
          !validMandatory(name.value) ||
          !validDOB(dob.value) ||
          !validMandatory(relationship.value) ||
          !validCID(cid.value) ||
          !validContactNumber(contactNo.value)
        ) {
          allValid = false;
        }
      });

    return allValid && totalPercentage === 100;
  }

  validatePersonalDetailForms() {
    let allValid = true;
    const personalDetails = this.personalDetailContainerTarget;
    const personalDetailUploads = this.personalDetailUploadContainerTarget;

    const email = personalDetails.querySelector(
      'input[name="[personal_detail][email_id]"]'
    );
    const connectNo = personalDetails.querySelector(
      'input[name="[personal_detail][contact_no]"]'
    );
    const issuedOn = personalDetails.querySelector(
      'input[name="[personal_detail][cid_issued_on]"]'
    );
    const expiresOn = personalDetails.querySelector(
      'input[name="[personal_detail][cid_expires_on]"]'
    );
    const passportPhoto = personalDetailUploads.querySelector(
      'input[name="[personal_detail][passport_photo]"]'
    );
    const signature = personalDetailUploads.querySelector(
      'input[name="[personal_detail][signature]"]'
    );
    const cidCopy = personalDetailUploads.querySelector(
      'input[name="[personal_detail][cid_copy]"]'
    );

    if (
      !validEmail(email.value) ||
      !validContactNumber(connectNo.value) ||
      !validIssueAndExpiresDate(issuedOn.value, expiresOn.value) ||
      !validFileUpload(passportPhoto.files[0]) ||
      !validFileUpload(signature.files[0]) ||
      !validFileUpload(cidCopy.files[0])
    ) {
      allValid = false;
    }
    return allValid;
  }

  validateIncomeDetailForms() {
    let allValid = true;

    const gross_annual_income = this.incomeDetailContainerTarget.querySelector(
      'input[name="[income][gross_annual_income]"]'
    );

    if (!validMandatory(gross_annual_income.value)) {
      allValid = false;
    }
    return allValid;
  }

  validateSpouseDetailForms() {
    let allValid = true;

    const spouseDetails = this.spouseDetailContainerTarget;
    if (
      spouseDetails.querySelector(
        'select[name="[spouse_detail][marital_status]"]'
      ).value === "Married"
    ) {
      const spouse_name = spouseDetails.querySelector(
        'input[name="[spouse_detail][name]"]'
      );
      const spouse_cid = spouseDetails.querySelector(
        'input[name="[spouse_detail][cid]"]'
      );
      const spouse_contact_no = spouseDetails.querySelector(
        'input[name="[spouse_detail][contact_no]"]'
      );
      const education_status = spouseDetails.querySelector(
        'select[name="[spouse_detail][education_status]"]'
      );
      const employment_detail = spouseDetails.querySelector(
        'select[name="[spouse_detail][employment_detail]"]'
      );
      const has_bnb_account = spouseDetails.querySelector(
        'select[name="[spouse_detail][has_bnb_account]"]'
      );
      const bnb_account_no = spouseDetails.querySelector(
        'input[name="[spouse_detail][bnb_account_no]"]'
      );

      if (
        !validMandatory(spouse_name.value) ||
        !validCID(spouse_cid.value) ||
        !validContactNumber(spouse_contact_no.value) ||
        !validMandatory(education_status.value) ||
        !validMandatory(employment_detail.value) ||
        !validMandatory(has_bnb_account.value) ||
        !this.validSpouseBnbAccount(has_bnb_account.value, bnb_account_no.value)
      ) {
        allValid = false;
      }
    }

    return allValid;
  }

  validSpouseBnbAccount(has_bnb_account, account_no) {
    return has_bnb_account.toLowerCase() === "yes"
      ? validBnbAccount(account_no)
      : true;
  }

  validateConsentLiability() {
    const liabilityCheckbox = document.getElementById("liability-check");

    if (liabilityCheckbox.checked) {
      return true;
    } else {
      return false;
    }
  }

  validatePrivacyPolicy() {
    const liabilityCheckbox = document.getElementById("privacy-check");

    if (liabilityCheckbox.checked) {
      return true;
    } else {
      return false;
    }
  }
}
